/* eslint-disable indent */
"use client";

import React, { startTransition, useEffect, useReducer, useState } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import dynamic from "next/dynamic";
import Footer from "@/components/molecules/Footer";
import { Header } from "@/components/molecules/Header";
import type { AppReducerAction, AppReducerState } from "@/types";
import { AppContext } from "lib/contexts";
import { appReducer } from "lib/reducers";
import { AppReducerActionType, UserPremiumType } from "types";

// QueryClient moved to module scope
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      // 5 minutes
      cacheTime: 1000 * 60 * 30,
      // 30 minutes
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      useErrorBoundary: false
    }
  }
});

// Properly type the lazy component
const EnhancedUIComponents = dynamic<{
  state: AppReducerState;
}>(() => import("./EnhancedUIComponents").then(mod => mod.default), {
  ssr: false,
  loading: () => null
});

// Move this outside of render cycle to avoid recreation
const initialState: AppReducerState = {
  showPricingTable: false,
  showSubscriptionTable: false,
  gold: 0,
  cookieConsentGiven: true,
  isLoggedIn: false,
  showAuthModal: false,
  userPremiumLevel: UserPremiumType.NONE,
  isOverlayVisible: false,
  username: "",
  email: "",
  referralCode: "",
  gclid: "",
  dailyLoginBonus: null,
  user: null,
  pendingReferenceImageUrl: null,
  pendingImageLoad: null
};

// Load basic user data quickly (~30ms)
async function loadBasicUserData(dispatch: React.Dispatch<AppReducerAction>) {
  try {
    const actions = await import("lib/actions/users");
    const {
      user
    } = await actions.getCurrentUserInfo();
    if (!user) return;

    // Use direct action type to avoid type issues
    dispatch({
      type: AppReducerActionType.MERGE_STATE,
      payload: {
        ...initialState,
        isLoggedIn: true,
        email: user.email ?? "",
        username: user.username ?? "",
        user: user,
        referralCode: user.referralCode ?? "",
        gold: user.gold ?? 0,
        userPremiumLevel: user.subscriptionType ?? UserPremiumType.NONE
      }
    });
    return {
      actions
    };
  } catch (error) {
    console.error("Failed to load basic user data:", error);
    return null;
  }
}

// Load full user data (potentially slower)
async function loadFullUserData(dispatch: React.Dispatch<AppReducerAction>, actions: any) {
  try {
    const {
      user: full
    } = await actions.getCurrentUserFullInfo();
    if (!full) return;
    dispatch({
      type: AppReducerActionType.MERGE_STATE,
      payload: {
        ...initialState,
        isLoggedIn: true,
        email: full.email ?? "",
        username: full.username ?? "",
        user: full,
        referralCode: full.referralCode ?? "",
        dailyLoginBonus: full.dailyGoldBonus ?? null,
        gold: full.gold ?? 0,
        userPremiumLevel: full.subscriptionType ?? UserPremiumType.NONE
      }
    });
  } catch (error) {
    console.error("Failed to load full user data:", error);
  }
}

// Split LayoutWrapper into core and enhanced versions
const LayoutWrapper = React.memo(function LayoutWrapper({
  children
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    // Mark hydration complete
    setHasHydrated(true);

    // Immediately load basic user data (~30ms)
    if (typeof window !== "undefined") {
      // Use requestAnimationFrame to wait for first paint
      requestAnimationFrame(() => {
        startTransition(() => {
          loadBasicUserData(dispatch).then(result => {
            if (!result) return;

            // Then load full user data after LCP or with a delay
            if ("PerformanceObserver" in window) {
              const lcpObserver = new PerformanceObserver(() => {
                lcpObserver.disconnect();
                startTransition(() => {
                  loadFullUserData(dispatch, result.actions);
                });
              });
              lcpObserver.observe({
                type: "largest-contentful-paint",
                buffered: true
              });

              // Fallback if LCP doesn't fire
              setTimeout(() => {
                lcpObserver.disconnect();
                startTransition(() => {
                  loadFullUserData(dispatch, result.actions);
                });
              }, 2000);
            } else {
              // Fallback for browsers without PerformanceObserver
              setTimeout(() => {
                startTransition(() => {
                  loadFullUserData(dispatch, result.actions);
                });
              }, 1000);
            }
          });
        });
      });
    }
  }, []);
  return <QueryClientProvider client={queryClient}>
            <AppContext.Provider value={{
      state,
      dispatch
    }}>
                <Header isLoggedIn={state.isLoggedIn} userPremiumLevel={state.userPremiumLevel} userEmail={state.email} />

                {/* Main content */}
                <main>{children}</main>

                <Footer />

                {/* Lazy-load secondary UI components after hydration */}
                {hasHydrated && <EnhancedUIComponents state={state} />}
            </AppContext.Provider>
        </QueryClientProvider>;
});
export default LayoutWrapper;